import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import ClassHeader from "../containers/_class/classHeader";
import Participants from "../containers/_class/participants";
import WaitingList from "../containers/_class/waitingList";
import CLASS from "../api/services/CLASS";
import WAITING_LIST from "../api/services/WAITING_LIST";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import styles from "../styles/_screens/class.module.scss";

const Class = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [members, setMembers] = useState([]);
  const [waitlistItems, setWaitlistItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const businessId = JSON.parse(localStorage.getItem("user_id"));

  // Fetch participants (returns appointment data + members)
  const fetchParticipants = useCallback(async () => {
    try {
      const data = await CLASS.get_participants_of_class(appointmentId);
      // The API returns { data: [...participants], appointment: {...} }
      // or it returns the participants array directly — handle both shapes
      if (Array.isArray(data)) {
        setMembers(data);
      } else if (data?.data) {
        setMembers(data.data);
        if (data.appointment) {
          setAppointment(data.appointment);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("שגיאה בטעינת משתתפים");
    }
  }, [appointmentId]);

  // Fetch appointment data separately (for header info)
  // The participants API might not return full appointment data,
  // so also fetch from the appointments list like the old cards.jsx did
  const fetchAppointment = useCallback(async () => {
    try {
      const payload = { id: businessId };
      const res = await APPOINTMENTS.get_appointments(payload);
      if (res.data?.data?.length > 0) {
        const myData = res.data.data.find((i) => i.id === businessId);
        if (myData) {
          const appt = myData.appoitnemts?.find(
            (i) => i._id === appointmentId
          );
          if (appt) setAppointment(appt);
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [appointmentId, businessId]);

  // Fetch waitlist for the class date + service
  const fetchWaitlist = useCallback(async () => {
    if (!appointment?.date) return;
    try {
      const dateStr = format(new Date(appointment.date), "yyyy-MM-dd");
      const serviceId =
        appointment.serviceId?._id || appointment.serviceId;
      const data = await WAITING_LIST.get_clients(businessId, dateStr, serviceId);
      // Backend now filters by date + serviceId, just use the data
      setWaitlistItems(data?.data || []);
    } catch (error) {
      console.log(error);
      // Waitlist fetch failure is non-critical, don't toast
    }
  }, [appointment?.date, appointment?.serviceId, businessId]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchParticipants(), fetchAppointment()]);
      setLoading(false);
    };
    init();
  }, [fetchParticipants, fetchAppointment]);

  // Fetch waitlist once we have appointment data (need the date)
  useEffect(() => {
    if (appointment?.date) {
      fetchWaitlist();
    }
  }, [appointment?.date, fetchWaitlist]);

  // Refresh handler — called after any mutation
  const handleDataChange = useCallback(() => {
    fetchParticipants();
    fetchWaitlist();
  }, [fetchParticipants, fetchWaitlist]);

  // Cancel entire class
  const handleCancelClass = async () => {
    if (!window.confirm("האם לבטל את כל השיעור? פעולה זו תבטל את כל התורים."))
      return;
    try {
      const res = await APPOINTMENTS.cancel_appointment({
        id: appointmentId,
        status: 3,
      });
      toast.success(res.data.message);
      navigate("/appointments");
    } catch (error) {
      console.log(error);
      toast.error("שגיאה בביטול השיעור");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className={styles.loading}>טוען...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <ClassHeader
          appointment={appointment}
          participantCount={members.length}
          onCancelClass={handleCancelClass}
        />
        <Participants
          members={members}
          appointmentId={appointmentId}
          onDataChange={handleDataChange}
        />
        <WaitingList
          waitlistItems={waitlistItems}
          onDataChange={handleDataChange}
          appointment={appointment}
        />
      </div>
    </Layout>
  );
};

export default Class;
